import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const CartCtx = createContext(null);
const STORAGE = "hdh_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE) || "[]");
      if (!Array.isArray(raw)) return [];
      return raw
        .filter((i) => i && typeof i.sku === "string")
        .map((i) => ({
          sku: i.sku,
          name: typeof i.name === "string" ? i.name : "Artikel",
          price: Number(i.price) || 0,
          image: typeof i.image === "string" ? i.image : "",
          compatible: typeof i.compatible === "string" ? i.compatible : "",
          qty: Math.max(1, Number(i.qty) || 1),
        }));
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE, JSON.stringify(items));
    } catch { /* private mode / quota — cart stays in memory */ }
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems((cur) => {
      const idx = cur.findIndex((i) => i.sku === product.sku);
      if (idx >= 0) {
        const cp = [...cur];
        cp[idx] = { ...cp[idx], qty: cp[idx].qty + qty };
        return cp;
      }
      return [...cur, {
        sku: product.sku, name: product.name, price: product.price,
        image: product.image, compatible: product.compatible || "", qty,
      }];
    });
    toast.success(`${product.name} hinzugefügt`, { description: `${qty}× im Warenkorb` });
  }, []);

  const remove = useCallback((sku) => setItems((cur) => cur.filter((i) => i.sku !== sku)), []);

  const updateQty = useCallback((sku, qty) => {
    if (qty <= 0) return remove(sku);
    setItems((cur) => cur.map((i) => i.sku === sku ? { ...i, qty } : i));
  }, [remove]);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const total = items.reduce((s, i) => s + i.qty * i.price, 0);

  return (
    <CartCtx.Provider value={{ items, count, total, add, remove, updateQty, clear, open, setOpen }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
